import { useCallback, useEffect, useRef, useState } from "react";

/**
 * RShield - Focus Loss Limit, Resume On Full Screen and Ban Reader.
 *
 * Counts how many times the reader has taken focus away from the RPD and
 * enforces the sender's configured limit.
 *
 * The counter listens to the native `blur` / `focus` / `visibilitychange`
 * events itself rather than deriving from React state. That matters: the RPD
 * re-renders heavily while a page is being blurred and unblurred, and any
 * counting driven off rendered state can miss an edge or double count when
 * two events land in the same batch. Here `awayRef` is a plain rising-edge
 * latch updated straight from the event, so one alt-tab is always exactly one
 * focus loss no matter how many events the browser fires or how React batches
 * the resulting renders.
 *
 * Behaviour driven by the sender's RShield options:
 *
 *   focusLossLimit = false
 *       Unlimited focus losses. Viewing pauses while away and resumes on
 *       return (or on returning to full screen when resumeOnFullScreen is set).
 *
 *   focusLossLimit = true, focusLossLimitValue = N
 *       The Nth focus loss revokes access: the RPD stays locked behind a
 *       non-dismissable overlay for the rest of the session. A value of 1
 *       means the very first focus loss revokes access.
 *
 *   resumeOnFullScreen = true
 *       After a focus loss the view does NOT auto-resume; it stays locked
 *       until the reader is back in full screen.
 *
 *   banReader = true
 *       A detected capture attempt locks the RPD for the rest of the session.
 *
 * The lock is session-scoped and client-side: it survives re-renders and
 * in-page navigation via sessionStorage, and is re-evaluated server-side by
 * the existing access checks on the next document open.
 *
 * @param {object} opts
 * @param {boolean}  opts.enabled            RShield is active for this document
 * @param {boolean}  opts.focusProtection    gate for counting focus losses
 * @param {boolean}  opts.focusLossLimit
 * @param {number}   opts.focusLossLimitValue
 * @param {boolean}  opts.resumeOnFullScreen
 * @param {boolean}  opts.banReader
 * @param {boolean}  opts.isFullscreen       current full screen state
 * @param {string}   opts.storageKey         session key (document + open id)
 * @param {(count:number, limit:number) => void} [opts.onFocusLoss]
 * @param {(reason:string, details:object) => void} [opts.onRevoked]
 */
export function useFocusLossLimit({
  enabled = false,
  focusProtection = false,
  focusLossLimit = false,
  focusLossLimitValue = 1,
  resumeOnFullScreen = false,
  banReader = false,
  isFullscreen = true,
  contentReady = true,
  storageKey = "",
  onFocusLoss,
  onRevoked,
} = {}) {
  const [focusLossCount, setFocusLossCount] = useState(0);
  const [isRevoked, setIsRevoked] = useState(false);
  // Why access was revoked - FocusLossLimitExceeded or CaptureDetected. The
  // reader is shown a different message for each, so this has to survive a
  // re-render and the sessionStorage restore.
  const [revokedReason, setRevokedReason] = useState("");
  // Set on a focus loss when resumeOnFullScreen is on; cleared once the reader
  // is back in full screen.
  const [awaitingFullScreen, setAwaitingFullScreen] = useState(false);

  const countRef = useRef(0);
  const awayRef = useRef(false);
  const revokedRef = useRef(false);
  const lastLossAtRef = useRef(0);
  const enteredFullScreenOnceRef = useRef(false);
  const suppressCountRef = useRef(0);
  const suppressUntilRef = useRef(0);
  const suppressTimerRef = useRef(null);
  const [isSuppressed, setIsSuppressed] = useState(false);
  // Counting only starts once the reader has actually had the RPD in focus.
  // Without this, opening the document in a background tab would register a
  // focus loss the reader never made.
  const armedRef = useRef(false);

  // Latest option values, read from inside the native event handlers so the
  // listeners never need re-binding (re-binding mid-session would drop the
  // rising-edge latch and could double count).
  const optionsRef = useRef({});
  optionsRef.current = {
    focusLossLimit,
    focusLossLimitValue,
    resumeOnFullScreen,
    contentReady,
    onFocusLoss,
    onRevoked,
  };

  const lockKey = storageKey ? `rshield_locked_${storageKey}` : "";
  const countKey = storageKey ? `rshield_focusloss_${storageKey}` : "";
  const storageRef = useRef({ lockKey, countKey });
  storageRef.current = { lockKey, countKey };

  // Restore any lock / count already recorded in this session.
  useEffect(() => {
    if (!enabled) return;
    try {
      // The stored value is the revoke reason. Older sessions stored "true",
      // which is still treated as locked - just without a specific reason.
      const storedLock = lockKey ? sessionStorage.getItem(lockKey) : null;
      if (storedLock) {
        revokedRef.current = true;
        setIsRevoked(true);
        setRevokedReason(storedLock === "true" ? "" : storedLock);
      }
      if (countKey) {
        const stored = parseInt(sessionStorage.getItem(countKey), 10);
        if (!isNaN(stored) && stored > 0) {
          countRef.current = stored;
          setFocusLossCount(stored);
        }
      }
    } catch (e) {
      /* sessionStorage unavailable - fall back to in-memory only */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, lockKey, countKey]);

  const revoke = useCallback((reason, details = {}) => {
    if (revokedRef.current) return;
    revokedRef.current = true;
    setIsRevoked(true);
    setRevokedReason(reason || "");
    setAwaitingFullScreen(false);
    try {
      const key = storageRef.current.lockKey;
      if (key) sessionStorage.setItem(key, reason || "true");
    } catch (e) {
      /* ignore */
    }
    const cb = optionsRef.current.onRevoked;
    if (typeof cb === "function") {
      try {
        cb(reason, details);
      } catch (e) {
        /* never let the host callback break protection */
      }
    }
  }, []);

  // --- In-app action suppression ---------------------------------------
  const suppressionActive = useCallback(() => {
    if (suppressCountRef.current > 0) return true;
    return Date.now() < suppressUntilRef.current;
  }, []);

  const beginInAppAction = useCallback((label = "", maxMs = 120000) => {
    suppressCountRef.current += 1;
    suppressUntilRef.current = Date.now() + maxMs;
    setIsSuppressed(true);
    // The reader is not away for the purposes of the latch either.
    awayRef.current = false;
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => {
      suppressCountRef.current = 0;
      suppressUntilRef.current = 0;
      setIsSuppressed(false);
    }, maxMs);
    return label;
  }, []);

  /** Ends the most recent in-app action, with a short grace period. */
  const endInAppAction = useCallback((graceMs = 1500) => {
    suppressCountRef.current = Math.max(suppressCountRef.current - 1, 0);
    if (suppressCountRef.current === 0) {
      suppressUntilRef.current = Date.now() + graceMs;
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = setTimeout(() => {
        suppressUntilRef.current = 0;
        awayRef.current = false;
        setIsSuppressed(false);
      }, graceMs);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    };
  }, []);

  /** Capture / screenshot attempt - honours the Ban Reader option. */
  const reportCaptureAttempt = useCallback(
    (details = {}) => {
      if (!enabled) return;
      if (optionsRef.current.contentReady !== true) return;
      if (banReader) revoke("CaptureDetected", details);
    },
    [enabled, banReader, revoke]
  );

  /**
   * Reads full screen straight from the DOM.
   *
   * The `isFullscreen` prop cannot be trusted for edge detection. useFullscreen
   * seeds its state to `true` (so that a disabled guard never blurs) and only
   * corrects it once its own effect has run, and it reports `true` for the
   * whole period before RShield has loaded and enabled it. Both of those look
   * like "the reader was in full screen" to a state based comparison, which is
   * how a phantom focus loss was being counted at document open. The DOM never
   * lies about this.
   */
  const readFullScreenNow = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  }, []);

  const registerLoss = useCallback(() => {
    if (revokedRef.current) return;
    if (!armedRef.current) return;
    if (suppressionActive()) return;

    const opts = optionsRef.current;

    if (opts.contentReady !== true) return;
    // "Resume on full screen": the RPD is blurred behind the "Enter Full
    // Screen" overlay until the reader enters full screen, so they have not
    // been reading and cannot have looked away from anything. Counting starts
    // at the first full screen entry, not at document open - otherwise a
    // reader who alt-tabs a few times while still on the overlay burns most of
    // the sender's allowance before ever seeing the document.
    if (opts.resumeOnFullScreen && !enteredFullScreenOnceRef.current) return;

    const now = Date.now();
    if (now - lastLossAtRef.current < 700) return;
    lastLossAtRef.current = now;

    const next = countRef.current + 1;
    countRef.current = next;
    setFocusLossCount(next);
    try {
      const key = storageRef.current.countKey;
      if (key) sessionStorage.setItem(key, String(next));
    } catch (e) {
      /* ignore */
    }

    if (typeof opts.onFocusLoss === "function") {
      try {
        opts.onFocusLoss(next, opts.focusLossLimit ? opts.focusLossLimitValue : 0);
      } catch (e) {
        /* ignore */
      }
    }

    if (opts.focusLossLimit && next >= opts.focusLossLimitValue) {
      revoke("FocusLossLimitExceeded", {
        FocusLossCount: next,
        FocusLossLimit: opts.focusLossLimitValue,
      });
      return;
    }

    if (opts.resumeOnFullScreen) setAwaitingFullScreen(true);
  }, [revoke, suppressionActive]);

  // --- Focus loss counting (native events) ------------------------------
  useEffect(() => {
    if (!enabled || !focusProtection) return undefined;

    const isAway = () => {
      if (document.hidden === true) return true;
      if (typeof document.hasFocus === "function") return !document.hasFocus();
      return false;
    };

    const registerLossFromNative = () => {
      // Rising edge only: one alt-tab that fires both blur and
      // visibilitychange must count once.
      if (awayRef.current) return;
      awayRef.current = true;
      registerLoss();
    };

    const registerReturn = () => {
      // Only clear the latch once the reader is genuinely back: `focus` can
      // fire while the tab is still hidden, and vice versa.
      if (!isAway()) awayRef.current = false;
    };

    const handleBlur = () => registerLossFromNative();
    const handleFocus = () => {
      if (!armedRef.current) {
        armedRef.current = true;
        awayRef.current = false;
        return;
      }
      registerReturn();
    };
    const handleVisibility = () => {
      if (document.hidden) registerLossFromNative();
      else handleFocus();
    };

    // Arm immediately when the reader already has the document in front.
    if (!isAway()) armedRef.current = true;

    window.addEventListener("blur", handleBlur, false);
    window.addEventListener("focus", handleFocus, false);
    document.addEventListener("visibilitychange", handleVisibility, false);

    return () => {
      window.removeEventListener("blur", handleBlur, false);
      window.removeEventListener("focus", handleFocus, false);
      document.removeEventListener("visibilitychange", handleVisibility, false);
    };
    // Bound once per document: every changing value is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, focusProtection, registerLoss]);

  useEffect(() => {
    if (!enabled || !focusProtection || !resumeOnFullScreen) return;
    if (!readFullScreenNow()) return;

    enteredFullScreenOnceRef.current = true;
    awayRef.current = false;
  }, [
    enabled,
    focusProtection,
    resumeOnFullScreen,
    isFullscreen,
    readFullScreenNow,
  ]);

  // Clear the "return to full screen" gate once the reader is back in full
  // screen. Never clears while revoked - that state is terminal.
  useEffect(() => {
    if (!enabled || !resumeOnFullScreen || revokedRef.current) {
      if (awaitingFullScreen && (!enabled || !resumeOnFullScreen)) {
        setAwaitingFullScreen(false);
      }
      return;
    }
    if (awaitingFullScreen && isFullscreen) setAwaitingFullScreen(false);
  }, [enabled, resumeOnFullScreen, awaitingFullScreen, isFullscreen]);

  const remaining =
    enabled && focusLossLimit
      ? Math.max(focusLossLimitValue - focusLossCount, 0)
      : null;

  return {
    focusLossCount,
    focusLossRemaining: remaining,
    isRevoked: enabled ? isRevoked : false,
    revokedReason: enabled ? revokedReason : "",
    awaitingFullScreen: enabled ? awaitingFullScreen : false,
    /** An in-app action is holding focus - do not blur, do not count. */
    isSuppressed: enabled ? isSuppressed : false,
    beginInAppAction,
    endInAppAction,
    revoke,
    reportCaptureAttempt,
  };
}

export default useFocusLossLimit;
